import { useMemo, useState } from 'react'
import {
  type LearnerAccount,
  type AccountsState,
  type LinkWarning,
  isLearnerAccount,
  validateLink,
  hasGatingWarning as computeHasGatingWarning,
  hasBlockingWarning as computeHasBlockingWarning,
} from '../../../accounts'
import { claimLearnerByLinkCode } from '../../../data/supabase/authService'
import { SUPABASE_AUTH_ENABLED } from '../../../data/supabase/session'

interface UseLinkChildOptions {
  accounts: AccountsState
  parentId: string
  linkedLearners: LearnerAccount[]
  linkParentToLearnerById: (parentId: string, learnerId: string) => void
}

interface UseLinkChildReturn {
  /** Current value of the koppelcode input. */
  linkCode: string
  setLinkCode: (code: string) => void
  /** Hard error from code lookup (no match, empty). NOT the same as warnings. */
  error: string
  /** Success message shown after a successful confirm. */
  success: string
  /** The learner the code resolved to, awaiting confirmation. */
  pendingLearner: LearnerAccount | null
  /**
   * Structured warnings from the unified linkage validator. Empty array
   * means the confirm CTA can be a normal primary button. Contains entries
   * (e.g. `multi_parent`) when the UI should surface a confirmation panel
   * and turn the CTA amber.
   */
  warnings: LinkWarning[]
  /**
   * True when at least one warning is gating-severity (`warning` or
   * `blocking`). UI should require explicit acknowledgment.
   */
  hasGatingWarning: boolean
  /**
   * True when a blocking warning is present and the UI MUST refuse to
   * proceed (e.g. `already_linked_self`). When this is true,
   * `handleConfirmLink` is a no-op.
   */
  hasBlockingWarning: boolean
  handleSubmit: (e: React.FormEvent) => void | Promise<void>
  handleConfirmLink: () => void
  handleCancelLink: () => void
}

/**
 * Hook driving the parent → learner koppel-flow.
 *
 * As of the linkage-domain unification, validation is delegated to the
 * canonical `validateLink({ kind: 'parent-learner', ... })` function, which
 * also surfaces a `multi_parent` warning when the learner is already linked
 * to other parents. The previous implementation showed only an informational
 * chip and let the confirm proceed silently — this version emits typed
 * warnings the UI can use to gate the confirm CTA.
 */
export function useLinkChild({
  accounts,
  parentId,
  linkedLearners,
  linkParentToLearnerById,
}: UseLinkChildOptions): UseLinkChildReturn {
  const [linkCode, setLinkCode] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [pendingLearner, setPendingLearner] = useState<LearnerAccount | null>(
    null,
  )

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccess('')
    setPendingLearner(null)

    const code = linkCode.trim().toUpperCase()
    if (!code) {
      setError('Vul een koppelcode in.')
      return
    }

    // ── Supabase-route ─────────────────────────────────────────────
    // De ouder zit op zijn EIGEN apparaat en heeft het account van het kind
    // dus niet lokaal staan. Zoeken in `accounts.accountsById` vond daarom
    // nooit iets — de koppeling kon alleen slagen als ouder en kind toevallig
    // dezelfde browser deelden.
    //
    // De server lost ook het kip-ei-probleem op: de ouder mag de profielrij
    // van het kind nog niet lezen vóórdat de koppeling bestaat.
    if (SUPABASE_AUTH_ENABLED) {
      const result = await claimLearnerByLinkCode(code)
      if (!result.ok || !result.learnerId) {
        setError(
          result.message ??
            'Geen leerling gevonden met deze koppelcode. Controleer de code en probeer opnieuw.',
        )
        return
      }
      // De koppeling staat nu in `parent_links`. De naam van het kind mag pas
      // ná die koppeling gelezen worden, dus tonen we hem hier zonder naam.
      setSuccess(
        'Gekoppeld! Je ziet dit kind terug in je overzicht zodra het scherm ververst.',
      )
      setLinkCode('')
      return
    }

    const learner = Object.values(accounts.accountsById).find(
      (a) => isLearnerAccount(a) && a.linkCode === code,
    ) as LearnerAccount | undefined

    if (!learner) {
      setError(
        'Geen leerling gevonden met deze koppelcode. Controleer de code en probeer opnieuw.',
      )
      return
    }

    // Already-linked is now a structured `already_linked_self` warning from
    // the validator instead of a free-form error string — but we keep the
    // friendly inline error too for the most common case, so the user
    // doesn't see a confirmation panel for a no-op.
    if (linkedLearners.some((l) => l.id === learner.id)) {
      setError('Deze leerling is al gekoppeld aan jouw account.')
      return
    }

    setPendingLearner(learner)
  }

  /**
   * Validate the pending link in real time so the confirmation panel can
   * react to state changes (e.g. another parent links in another tab).
   */
  const warnings = useMemo<LinkWarning[]>(() => {
    if (!pendingLearner) return []
    return validateLink(accounts, {
      kind: 'parent-learner',
      parentId,
      learnerId: pendingLearner.id,
    })
  }, [pendingLearner, accounts, parentId])

  const hasGatingWarning = useMemo(
    () => computeHasGatingWarning(warnings),
    [warnings],
  )

  const hasBlockingWarning = useMemo(
    () => computeHasBlockingWarning(warnings),
    [warnings],
  )

  const handleConfirmLink = () => {
    if (!pendingLearner) return
    // Defensive: blocking warnings (e.g. already_linked_self) must not
    // proceed even if the UI somehow lets the user click the confirm.
    if (hasBlockingWarning) return
    linkParentToLearnerById(parentId, pendingLearner.id)
    setSuccess(`${pendingLearner.displayName} is succesvol gekoppeld!`)
    setLinkCode('')
    setPendingLearner(null)
  }

  const handleCancelLink = () => {
    setPendingLearner(null)
  }

  return {
    linkCode,
    setLinkCode,
    error,
    success,
    pendingLearner,
    warnings,
    hasGatingWarning,
    hasBlockingWarning,
    handleSubmit,
    handleConfirmLink,
    handleCancelLink,
  }
}
